import Phaser from 'phaser';
import type { HitQuality } from '@/utils/timing';
import { THEME } from '@/config/theme';

export class VFXManager {
  private scene: Phaser.Scene;
  private particles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private streakGlow: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createParticleTexture();
  }

  private createParticleTexture(): void {
    // Create a simple particle texture if it doesn't exist
    if (!this.scene.textures.exists('particle')) {
      const graphics = this.scene.make.graphics({ x: 0, y: 0 });
      graphics.fillStyle(0xffffff, 1);
      graphics.fillCircle(8, 8, 8);
      graphics.generateTexture('particle', 16, 16);
      graphics.destroy();
    }
  }

  /**
   * Show hit effect at position
   */
  showHitEffect(x: number, y: number, quality: HitQuality): void {
    const colors: Record<HitQuality, number> = {
      perfect: THEME.colors.hit.perfectHex,
      good: THEME.colors.hit.goodHex,
      ok: THEME.colors.hit.okHex,
      bad: THEME.colors.hit.badHex,
    };

    const color = colors[quality];
    const count = quality === 'perfect' ? 20 : quality === 'good' ? 12 : 8;

    // Burst of particles
    const emitter = this.scene.add.particles(x, y, 'particle', {
      speed: { min: 100, max: 200 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 400,
      tint: color,
      emitting: false,
    });

    emitter.explode(count);

    // Clean up after animation
    this.scene.time.delayedCall(500, () => {
      emitter.destroy();
    });

    // Add expanding ring for perfect hits
    if (quality === 'perfect') {
      this.showPerfectRing(x, y);
    }
  }

  private showPerfectRing(x: number, y: number): void {
    const ring = this.scene.add.circle(x, y, 20, THEME.colors.primaryHex, 0);
    ring.setStrokeStyle(3, THEME.colors.primaryHex, 1);

    this.scene.tweens.add({
      targets: ring,
      radius: 60,
      alpha: 0,
      duration: 300,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        ring.setStrokeStyle(3, THEME.colors.primaryHex, ring.alpha);
      },
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Show effect when note passes unhit
   */
  showPassedEffect(x: number, y: number): void {
    // Red X mark
    const size = 30;
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(4, THEME.colors.hit.badHex, 1);
    graphics.lineBetween(x - size/2, y - size/2, x + size/2, y + size/2);
    graphics.lineBetween(x + size/2, y - size/2, x - size/2, y + size/2);

    this.scene.tweens.add({
      targets: graphics,
      alpha: 0,
      y: y - 20,
      duration: 500,
      onComplete: () => graphics.destroy(),
    });
  }

  /**
   * Update streak visuals
   */
  updateStreakEffect(_streak: number, _hitZoneX: number, _trackY: number): void {
    // Remove old glow
    if (this.streakGlow) {
      this.streakGlow.destroy();
      this.streakGlow = null;
    }
    // No streak-based glow; keep hit zone visuals stable.
    return;
  }

  /**
   * Show streak milestone celebration
   */
  showStreakMilestone(streak: number, x: number, y: number): void {
    // Firework-like burst
    const colors = [THEME.colors.primaryHex, THEME.colors.cyanHex, THEME.colors.yellowHex];

    for (let i = 0; i < 3; i++) {
      this.scene.time.delayedCall(i * 100, () => {
        const emitter = this.scene.add.particles(x, y, 'particle', {
          speed: { min: 150, max: 300 },
          scale: { start: 0.6, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan: 600,
          tint: colors[i % colors.length],
          emitting: false,
        });

        emitter.explode(15);

        this.scene.time.delayedCall(700, () => {
          emitter.destroy();
        });
      });
    }
  }

  /**
   * Show combo break effect
   */
  showComboBreak(): void {
    return;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.streakGlow) {
      this.streakGlow.destroy();
    }
    if (this.particles) {
      this.particles.destroy();
    }
  }
}
